import { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';

export interface SurveyEmissionRowLine {
  key: string;
  name: string;
  units: string;
  kg: string;
}

interface Props {
  unitsLabel: string;
  kgLabel: string;
  rows: SurveyEmissionRowLine[];
  totalLabel: string;
  totalKg: string;
  /** Optional control rendered inside the total row, next to the total figure. */
  totalAction?: ReactNode;
}

const colLabel = { width: 200, flexShrink: 0, textAlign: 'right' } as const;
const rowSx = { p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 };
const totalRowSx = { px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' };

/** Rule-per-row emissions table: rule name, units, kg CO2e, with a total row. */
export const SurveyEmissionRows = ({ unitsLabel, kgLabel, rows, totalLabel, totalKg, totalAction }: Props) => (
  <Stack spacing={0.75}>
    <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ px: 1.5 }}>
      <Typography variant='caption' color='text.secondary' fontWeight={600}>Emission rule</Typography>
      <Stack direction='row' spacing={0}>
        <Typography variant='caption' color='text.secondary' fontWeight={600} sx={colLabel}>{unitsLabel}</Typography>
        <Typography variant='caption' color='text.secondary' fontWeight={600} sx={{ ...colLabel, ml: 2 }}>{kgLabel}</Typography>
      </Stack>
    </Stack>

    {rows.map(row => (
      <Box key={row.key} sx={rowSx}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
          <Typography variant='body2' fontWeight={600}>{row.name}</Typography>
          <Stack direction='row' spacing={0}>
            <Typography variant='body2' color='text.secondary' sx={colLabel}>{row.units}</Typography>
            <Typography variant='body2' color='text.secondary' sx={{ ...colLabel, ml: 2 }}>{row.kg}</Typography>
          </Stack>
        </Stack>
      </Box>
    ))}

    <Box sx={totalRowSx}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
        <Typography variant='body2' fontWeight={700}>{totalLabel}</Typography>
        <Stack direction='row' spacing={1} alignItems='center'>
          {totalAction}
          <Typography variant='body2' fontWeight={700} sx={{ ...colLabel, ml: 1 }}>{totalKg}</Typography>
        </Stack>
      </Stack>
    </Box>
  </Stack>
);
